import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import type { DocumentMetadataUserEditEntry } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { parseDocumentMetadataUserEditEntries } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { documentMetadataUserEditsByEntryIds } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { documentMetadataUserEditEntryId } from 'features/knowledgeDocumentViewer/utils/metadataUserEditIds'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'

export type UsePageRevisionFamilyCascadeTargetsArgs = {
  documentIds: readonly string[]
  enabled?: boolean
}

export type UsePageRevisionFamilyCascadeTargetsResult = {
  priorDocumentEditsByBqDocumentId: ReadonlyMap<
    string,
    DocumentMetadataUserEditEntry
  >
  mutateCascadePriorEdits: () => Promise<unknown>
  areCascadePriorEditsReady: boolean
  isCascadeLoading: boolean
}

/**
 * Loads document prior-edits used when cascading page family saves onto
 * Document Details. Kept separate from revision-family discovery/filtering.
 */
export function usePageRevisionFamilyCascadeTargets({
  documentIds,
  enabled = true,
}: UsePageRevisionFamilyCascadeTargetsArgs): UsePageRevisionFamilyCascadeTargetsResult {
  const { siteID } = useKnowledgeDocumentDataContext()

  const documentPriorEditLoadOptions = useMemo(() => {
    if (!enabled || !siteID || documentIds.length === 0) return undefined
    const entryIds = documentIds.map((id) =>
      documentMetadataUserEditEntryId(siteID, id)
    )
    return documentMetadataUserEditsByEntryIds(entryIds, { site: siteID })
  }, [documentIds, enabled, siteID])

  const {
    data: rawDocumentPriorEdits,
    isLoading: isLoadingDocumentPriorEdits,
    mutate: mutateCascadePriorEdits,
  } = useGetRolodexEntriesByLoadOptions<unknown>(documentPriorEditLoadOptions)

  const priorDocumentEditsByBqDocumentId = useMemo(() => {
    const map = new Map<string, DocumentMetadataUserEditEntry>()
    if (rawDocumentPriorEdits === undefined || !siteID) return map
    const edits = parseDocumentMetadataUserEditEntries(rawDocumentPriorEdits)
    const editsById = new Map(edits.map((edit) => [edit.entry.id, edit]))
    for (const id of documentIds) {
      const entryId = documentMetadataUserEditEntryId(siteID, id)
      const edit = editsById.get(entryId)
      if (edit) map.set(id, edit)
    }
    return map
  }, [documentIds, rawDocumentPriorEdits, siteID])

  const isCascadeLoading =
    documentPriorEditLoadOptions !== undefined && isLoadingDocumentPriorEdits

  const areCascadePriorEditsReady =
    documentPriorEditLoadOptions === undefined ||
    rawDocumentPriorEdits !== undefined

  return {
    priorDocumentEditsByBqDocumentId,
    mutateCascadePriorEdits,
    areCascadePriorEditsReady,
    isCascadeLoading,
  }
}
